import ParameterValidator from '@mvps-genesis/parameter-validator';
import EventBus from '@mvps-genesis/event-bus';

/*
 *--------------------------------------------------------------------------
 * Tabs
 *--------------------------------------------------------------------------
 *
 * Provides an accessible solution for tabs. The tab HTML structure must be
 * modeled according to the tabs design pattern articles listed below and
 * must contain the role and aria attributes.
 *
 * https://www.w3.org/TR/wai-aria-practices-1.1/#tabpanel
 * https://www.w3.org/TR/wai-aria-practices-1.1/examples/tabs/tabs-1/tabs.html
 *
 */
export default class Tabs {

	/**
	 * Parameter Validator class object
	 *
	 * @var	{ParameterValidator} @mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Mapping of tab selectors
	 *
	 * @var {Object}
	 */
	#selectors = {
		tab: '[role="tab"]',
		tabList: '[role="tablist"]',
		tabPanel: '[role="tabpanel"]'
	};

	/**
	 * Directional mapping based on key input
	 *
	 * @var {Object}
	 */
	#direction = {
		arrowdown: 1,
		arrowleft: -1,
		arrowright: 1,
		arrowup: -1
	};

	/**
	 * Tabs container element
	 *
	 * @var {HTMLElement}
	 */
	containerElement;

	/**
	 * The tablist element (role="tablist")
	 *
	 * @var {HTMLElement}
	 */
	tabListElement;

	/**
	 * List of objects containing the tab elements.
	 *
	 * @var {Array}
	 *
	 * Each object has the following properties:
	 * @prop {HTMLElement} tab - The tab element (role="tab")
	 * @prop {HTMLElement} tabpanel - The tab element's associated panel element (role="tabpanel")
	 */
	tabItems;

	/**
	 * Flag to determine if tab auto activation is enabled or disabled
	 *
	 * @var {Boolean}
	 */
	autoActivate;

	/**
	 * Auto activation delay (ms)
	 *
	 * @var {Int}
	 */
	autoActivateDelay;

	/**
	 * Create a new Tabs object.
	 *
	 * @param  {HTMLElement|String}  containerElement          The tabs container element.
	 * @param  {Boolean}             options.autoActivate      Flag to determine if tab auto activation is enabled or disabled. Defaults to true.
	 * @param  {Int}                 options.autoActivateDelay Auto activation delay (ms). Applied when autoActivate option is enabled. Defaults to 300.
	 */
	constructor (
		containerElement,
		{
			autoActivate = true,
			autoActivateDelay = 300
		} = {}
	) {
		this.#validator = new ParameterValidator(this);
		this.containerElement = this.#validator.isElement('containerElement', containerElement);
		this.tabListElement = this.#validator.isElement('tabListElement', this.#selectors.tabList, {container: this.containerElement});
		this.autoActivate = this.#validator.isBoolean('options.autoActivate', autoActivate);
		this.autoActivateDelay = this.#validator.isNumber('options.autoActivateDelay', autoActivateDelay);
		this.tabItems = this.setTabItems();
	}

	/**
	 * Set the tab items list.
	 */
	setTabItems () {
		const tabItems = [];
		const tabElements = this.#validator.isElementList('setTabElements.tabElements', this.#selectors.tab, {container: this.containerElement});

		for (const tabElement of tabElements) {
			const panelElement = this.#validator.isElement('setTabElements.panelElement', `#${tabElement.getAttribute('aria-controls')}${this.#selectors.tabPanel}`, {container: this.containerElement});

			this.addTabElementListeners(tabElement);

			tabItems.push({
				tab: tabElement,
				tabpanel: panelElement
			});
		}

		return tabItems;
	}

	/**
	 * Add tab element event listeners.
	 *
	 * @param {HTMLElement} tabElement The tab element.
	 */
	addTabElementListeners (tabElement) {
		tabElement.addEventListener('click', this.onTabElementClick);
		tabElement.addEventListener('keydown', this.onTabElementKeydown);
		tabElement.addEventListener('keyup', this.onTabElementKeyup);

		if (this.autoActivate) {
			tabElement.addEventListener('focus', this.onTabElementFocus);
		}
	}

	/**
	 * Remove tab element event listeners.
	 *
	 * @param  {HTMLElement} tabElement The tab element.
	 */
	removeTabElementListeners (tabElement) {
		tabElement.removeEventListener('click', this.onTabElementClick);
		tabElement.removeEventListener('keydown', this.onTabElementKeydown);
		tabElement.removeEventListener('keyup', this.onTabElementKeyup);

		if (this.autoActivate) {
			tabElement.removeEventListener('focus', this.onTabElementFocus);
		}
	}

	/* eslint-disable no-invalid-this */
	/**
	 * Tab element click event handling.
	 *
	 * @param  {Event} event The click event.
	 */
	onTabElementClick = (event) => {
		this.activateTab(event.target, false);
	}
	/* eslint-enable no-invalid-this */

	/* eslint-disable no-invalid-this */
	/**
	 * Tab element keydown event handling.
	 *
	 * @param  {Event} event The keydown event.
	 */
	onTabElementKeydown = (event) => {
		switch (event.key.toLowerCase()) {
			case 'arrowdown':
			case 'arrowup':
				if (this.isVerticalTabList()) {
					event.preventDefault();

					this.onTabElementArrowKey(event);
				}

				break;

			case 'end':
				event.preventDefault();

				if (this.autoActivate) {
					this.activateTab(this.tabItems[this.tabItems.length - 1]);
				} else {
					this.focusLastTab();
				}

				break;

			case 'home':
				event.preventDefault();

				if (this.autoActivate) {
					this.activateTab(this.tabItems[0]);
				} else {
					this.focusFirstTab();
				}

				break;

			default:
				break;
		}
	}
	/* eslint-enable no-invalid-this */

	/* eslint-disable no-invalid-this */
	/**
	 * Tab element keyup event handling.
	 *
	 * @param  {Event} event The keyup event.
	 */
	onTabElementKeyup = (event) => {
		switch (event.key.toLowerCase()) {
			case 'arrowdown':
			case 'arrowup':
				if (this.autoActivate && this.isVerticalTabList()) {
					event.preventDefault();

					this.onTabElementFocus(event);
				}

				break;

			case 'arrowleft':
			case 'arrowright':
				if (!this.isVerticalTabList()) {
					event.preventDefault();

					this.onTabElementArrowKey(event);
				}

				break;

			case 'enter':
			case ' ':
			case 'spacebar':
				if (!this.autoActivate) {
					this.activateTab(event.target);
				}

				break;

			default:
				break;
		}
	}
	/* eslint-enable no-invalid-this */

	/* eslint-disable no-invalid-this */
	/**
	 * Tab element arrow key event handling.
	 *
	 * @param  {Event} event The arrow key event.
	 */
	onTabElementArrowKey = (event) => {
		const key = event.key.toLowerCase();

		if (!this.#direction[key]) {
			return;
		}

		const targetTabItem = this.tabItems[this.getTabItemIndexByTabElement(event.target) + this.#direction[key]];

		if (targetTabItem) {
			targetTabItem.tab.focus();
		} else if (key === 'arrowleft' || key === 'arrowup') {
			this.focusLastTab();
		} else if (key === 'arrowright' || key === 'arrowdown') {
			this.focusFirstTab();
		}
	}
	/* eslint-enable no-invalid-this */

	/* eslint-disable no-invalid-this */
	/**
	 * Tab element focus event handling.
	 *
	 * @param  {Event} event The focus event.
	 */
	onTabElementFocus = (event) => {
		setTimeout(() => {
			const tabItem = this.getTabItemByTabElement(event.target);

			if (tabItem && tabItem.tab === document.activeElement) {
				this.activateTab(tabItem, false);
			}
		}, this.autoActivateDelay);
	}
	/* eslint-enable no-invalid-this */

	/**
	 * Set focus on the first tab item's tab element.
	 */
	focusFirstTab () {
		this.tabItems[0].tab.focus();
	}

	/**
	 * Set focus on the last tab item's tab element.
	 */
	focusLastTab () {
		this.tabItems[this.tabItems.length - 1].tab.focus();
	}

	/**
	 * Tab activation handling.
	 *
	 * @param  {HTMLElement}     tabElement The tab element.
	 * @param  {Boolean}         setFocus   Set focus on the tab element on activation.
	 * @return {Undefined|void}             Return undefined when targeted tab is already the active tab.
	 */
	activateTab (tabElement, setFocus = true) {
		const tabItem = this.isTabItem(tabElement) ? tabElement : this.getTabItemByTabElement(tabElement);

		if (!tabItem) {
			throw new TypeError(`${this.constructor.name}::activateTab Invalid 'tabElement' provided`);
		}

		if (tabItem.tab.getAttribute('aria-selected') === 'true') {
			if (setFocus) {
				tabItem.tab.focus();
			}

			return;
		}

		this.deactivateTabs();

		tabItem.tab.removeAttribute('tabindex');
		tabItem.tab.setAttribute('aria-selected', 'true');

		tabItem.tabpanel.removeAttribute('hidden');

		if (setFocus) {
			tabItem.tab.focus();
		}

		EventBus.dispatchEvent('MVPS:activeTab', {
			tabElement: tabItem.tab,
			tabListElement: this.tabListElement,
			tabPanelElement: tabItem.tabpanel
		});
	}

	/**
	 * Tabs deactivation handling.
	 */
	deactivateTabs () {
		for (const tabItem of this.tabItems) {
			tabItem.tab.setAttribute('tabindex', '-1');
			tabItem.tab.setAttribute('aria-selected', 'false');

			tabItem.tabpanel.setAttribute('hidden', '');
		}
	}

	/**
	 * Check if item is a tab item.
	 *
	 * @param  {Object}  object The item to check.
	 */
	isTabItem (object) {
		return [
			'tab',
			'tabpanel'
		].every((property) => {
			return object.hasOwnProperty(property) && object[property] instanceof HTMLElement;
		});
	}

	/**
	 * Determine if the tab list element is a vertical tab list.
	 *
	 * @return {Boolean}
	 */
	isVerticalTabList () {
		return this.tabListElement && this.tabListElement.getAttribute('aria-orientation') === 'vertical';
	}

	/**
	 * Retrieve a tab item index by tab element.
	 *
	 * @param  {HTMLElement} tabElement The tab element.
	 * @return {Int}
	 */
	getTabItemIndexByTabElement (tabElement) {
		return this.tabItems.findIndex((item) => {
			return item.tab === tabElement;
		});
	}

	/**
	 * Retrieve a tab item by tab element.
	 *
	 * @param  {HTMLElement} tabElement The tab element.
	 * @return {Object}
	 */
	getTabItemByTabElement (tabElement) {
		return this.tabItems.find((item) => {
			return item.tab === tabElement;
		});
	}

	/**
	 * Retrieve a tab item index by tabpanel element.
	 *
	 * @param  {HTMLElement} panelElement The tabpanel element.
	 * @return {Int}
	 */
	getTabItemIndexByPanelElement (panelElement) {
		return this.tabItems.findIndex((item) => {
			return item.tabpanel === panelElement;
		});
	}

	/**
	 * Retrieve a tab item by tabpanel element.
	 *
	 * @param  {HTMLElement} panelElement The tabpanel element.
	 * @return {Object}
	 */
	getTabItemByPanelElement (panelElement) {
		return this.tabItems.find((item) => {
			return item.tabpanel === panelElement;
		});
	}

	/**
	 * Retrieve the #selectors property.
	 *
	 * @return {Object}
	 */
	getSelectors () {
		return this.#selectors;
	}

	/**
	 * Retrieve the #direction property.
	 *
	 * @return {Object}
	 */
	getDirection () {
		return this.#direction;
	}
}
